import { Flex } from 'antd';
import styled, { css } from 'styled-components';
import { FOCUS_HALO_REACH, focusHaloBleed } from './scrollGutter';
import { atPaneWidth, WIDE_PANE_MIN_WIDTH } from './paneWidth';

/**
 * Collapses individually bordered rows into a single card: rows sit flush, adjacent
 * rows share one hairline, and only the outer corners stay rounded.
 *
 * `row` is the class applied to each row element.
 */
const stackedRowStyles = (row: string) => css`
  display: flex;
  flex-direction: column;

  ${row} {
    border: 1px solid var(--clr-border-grey-100);
    background-color: var(--clr-container-bg);
    /* Rows pull up by the border width so neighbours share one hairline
       instead of stacking two. */
    margin-bottom: -1px;
    position: relative;

    &:first-child {
      border-top-left-radius: 5px;
      border-top-right-radius: 5px;
    }
    &:last-child {
      border-bottom-left-radius: 5px;
      border-bottom-right-radius: 5px;
      margin-bottom: 0;
    }
  }
`;

export const DefaultViewList = styled.div`
  ${stackedRowStyles('.default-view-row')}

  .default-view-row {
    padding: 10px;
  }
`;

export const FeatureList = styled.div`
  ${stackedRowStyles('.feature-row')}

  .feature-row {
    padding: 15px 10px;
    transition: background-color 0.3s ease-in-out;

    &.always-on {
      background-color: var(--selected);
    }

    &:not(.always-on):hover {
      background-color: var(--clr-general-hover);
    }
  }

  .feature-row.always-on + .feature-row.always-on {
    border-top-color: var(--clr-body-bg);
  }

  .always-on-badge {
    color: var(--clr-general-primary);
    .icon {
      color: var(--clr-general-primary);
      font-size: 12px;
    }
  }
`;

/** The feature list and the setup fields scroll together above the Continue button. */
export const WorkspaceSetupScroll = styled(Flex)`
  ${focusHaloBleed}
  /* Room for the fields' focus ring at the scroll box's top and bottom edges too. */
  padding-block: ${FOCUS_HALO_REACH};
  margin-block: calc(-1 * ${FOCUS_HALO_REACH});
`;

/** Start module and Appearance share a row on a wide pane and stack on a narrow one. */
export const WorkspaceSetupFields = styled(Flex)`
  flex-direction: column;
  ${atPaneWidth(
    'min-width',
    WIDE_PANE_MIN_WIDTH,
    css`
      flex-direction: row;
      > * {
        flex: 1 1 0;
        min-width: 0;
      }
    `,
  )}
`;

/** A bordered button that opens a picker: tinted on hover, primary-bordered while its picker is open. */
export const PickerTrigger = styled.button`
  border: 1px solid var(--clr-border-grey-100);
  border-radius: 5px;
  background-color: var(--clr-container-bg);
  font: inherit;
  overflow-wrap: anywhere;
  transition:
    background-color 0.2s ease-in-out,
    border-color 0.2s ease-in-out;

  &:hover {
    border-color: var(--clr-general-border-hover);
    background-color: var(--clr-general-hover);
  }
  &[aria-expanded='true'] {
    border-color: var(--clr-general-primary);
    background-color: var(--selected);
    &:hover {
      background-color: var(--selected-hover);
    }
  }
  &:focus-visible {
    outline: 2px solid var(--clr-general-primary);
    outline-offset: 1px;
  }

  .picker-chevron {
    transition: transform 0.2s ease-in-out;
  }
  &[aria-expanded='true'] .picker-chevron {
    transform: rotate(180deg);
  }
`;

/** Matches the popup to its field: antd only gives it the field's width as a minimum. */
export const StartModulePanel = styled(Flex)`
  contain: inline-size;
  border-radius: 5px;
  background-color: var(--clr-container-bg);
  box-shadow: 0px 0px 30px 0px var(--clr-shadow);
  outline: none;
`;

export const StartModuleOptions = styled(Flex)`
  &:focus-visible {
    outline: 2px solid var(--clr-general-primary);
    outline-offset: -2px;
    border-radius: 5px;
  }
`;

export const StartModuleOption = styled(Flex)`
  min-height: 40px;
  border-radius: 5px;
  overflow-wrap: anywhere;
  color: var(--clr-title);

  &[data-active='true'] {
    background-color: var(--clr-general-hover);
  }
  &[aria-selected='true'] {
    background-color: var(--selected);
    color: var(--clr-general-primary);
    &[data-active='true'] {
      background-color: var(--selected-hover);
    }
  }
`;

export const StartModuleNote = styled(Flex)`
  border-top: 1px solid var(--clr-border-grey-100);

  /* Centres the 12px icon on the note's first 16px line, so it stays at the top when the note wraps. */
  .icon {
    margin-block-start: 2px;
  }
`;

/** The accent colour swatch shown as the Appearance field's icon. */
export const ThemeColorDot = styled.span`
  width: 20px;
  height: 20px;
  /* Ring and dot are one box: a nested dot rounds to pixels on its own and drifts off centre at Small and Large. */
  padding: 2px;
  border: 1px solid var(--clr-general-primary);
  border-radius: 50%;
  background-color: var(--clr-general-primary);
  background-clip: content-box;
`;
